// Alvo de seek SEGURO: nunca não-finito, nunca negativo, nunca no fim (ou além).
//
// ── Por que existe ──
// `WebPlayer.seek(p)` fazia `video.currentTime = p` cru. Os quatro callers (série,
// VOD, catch-up, mobile) usam o `seek` pra RETOMAR (`seek(resumeFrom)` no `ready`).
// Um alvo além do fim faz o motor prender a posição NO fim (spec HTML: seek além do
// fim vira o fim), a mídia fica `ended` e o player de série trata como "episódio
// acabou" — fecha na hora. NaN/Infinity como alvo não têm significado.
//
// ── Isto é DEFESA, não o conserto do "não carrega" ──
// O retomar de um item `finished` nem passa `resumeAt` (gate da ficha), e com o
// conserto do Infinity (apps/tv lib/playback-progress) nenhuma posição sem duração
// finita é mais gravada. Não há, hoje, caminho medido que entregue um alvo além do
// fim. O clamp fecha a classe pra quando houver.

/**
 * Margem antes do fim pro alvo de um seek, em segundos.
 *
 * Derivação: o spec HTML só considera a mídia terminada com a posição NO fim
 * ("ended playback"), então qualquer margem > 0 mantém o alvo estritamente dentro
 * da mídia. 1s é a escolha mínima — NÃO medida em aparelho: motor que faz seek por
 * keyframe pode arredondar pra frente e ainda encostar no fim. Margem maior seria
 * decisão de PRODUTO (quanto do fim reassistir), que não é deste pacote.
 */
export const SEEK_END_MARGIN_SEC = 1

/** Duração utilizável pra limitar um seek: finita e > 0. NaN (antes do metadata) e
 *  Infinity (live/sem fim declarado) são "desconhecida". */
export function isKnownDuration(durationSec: number): boolean {
  return Number.isFinite(durationSec) && durationSec > 0
}

/**
 * Alvo seguro pro seek, ou `null` = NÃO mexer no `currentTime`.
 *
 * - posição não-finita (NaN, ±Infinity) → `null`;
 * - negativa → 0;
 * - duração conhecida → no máximo `duração − margem` (e nunca abaixo de 0);
 * - duração desconhecida → a posição como veio (quem limita é o recheck no metadata).
 */
export function clampSeekTarget(positionSec: number, durationSec: number): number | null {
  if (!Number.isFinite(positionSec)) return null
  const p = positionSec < 0 ? 0 : positionSec
  if (!isKnownDuration(durationSec)) return p
  const limit = durationSec - SEEK_END_MARGIN_SEC
  return p > limit ? (limit > 0 ? limit : 0) : p
}
